/**
 * Actor links (MECHANICS "Reader interactions"): a source, rule, topic, model or connector shown in the
 * reader opens that exact Settings record; following one pushes history, so Back returns to the reader
 * with its scroll position (scroll-memory.ts).
 */

import type { Actor } from '@digester/core';
import type { Lookups } from './data.js';

export interface ActorLink {
  /** What to show. */
  label: string;
  /** Settings path, or null when the actor has no record (you, Digester housekeeping). */
  to: string | null;
}

const enc = encodeURIComponent;

export const settingsPath = {
  source: (id: string) => `/settings/sources/${enc(id)}`,
  filter: (id: string) => `/settings/filters/${enc(id)}`,
  scoringRule: (id: string) => `/settings/models/${enc(id)}`,
  topic: (id: string) => `/settings/topics/${enc(id)}`,
  jev: () => '/settings/models/jev',
  model: (id: string) => `/settings/models/${enc(id)}`,
  assistant: () => '/settings/models/assistant',
  connector: (id: string) => `/settings/mcps/${enc(id)}`,
};

/** Who an actor is, for "Moved by" and the timeline. */
export function actorLink(actor: Actor, lookups: Lookups): ActorLink {
  switch (actor.kind) {
    case 'user':
      return { label: 'You', to: null };
    case 'system':
      return { label: 'Digester', to: null };
    case 'source': {
      const s = lookups.source(actor.id);
      return { label: s?.name ?? 'Source', to: actor.id ? settingsPath.source(actor.id) : null };
    }
    case 'filter': {
      const r = lookups.rule(actor.id);
      return actor.id ? { label: r?.name ?? 'Deleted filter', to: settingsPath.filter(actor.id) } : { label: 'Filters', to: null };
    }
    case 'scoring': {
      if (!actor.id) return { label: 'Jev', to: settingsPath.jev() };
      const r = lookups.rule(actor.id);
      return { label: r?.name ?? 'Deleted scoring rule', to: settingsPath.scoringRule(actor.id) };
    }
    case 'mcp':
      return { label: 'Your assistant', to: actor.id ? settingsPath.connector(actor.id) : null };
  }
}
